'use client';

import React, { type HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useIsTabletOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  type MemSearchProductDataWithDistance,
  type RecommendedHotelData,
  RecommendedHotelsList,
} from '@/app/components/hotel-detail/RecommendedHotels';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import ScrollableHorizontal, {
  MainContainer,
  ScrollableHorizontalWithDots,
} from '@/app/components/hotel-detail/ScrollableHorizontal';
import ConditionWrapper from '@/app/components/hotel-detail/ConditionWrapper';
import { trackHotelCardClick } from '@/app/components/hotel-detail/trackInteractionEvents';
import { directLineDistance } from '@/app/components/hotel-detail/common/common.helper';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { SubSourceType } from '@/app/common/starTrack/events/searchRequested';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getDistanceFromKm, getDistanceFromM } from '@/app/common/measures/getters.ts';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { useRecommendedHotels } from './hooks/useRecommendedHotels';
import { useIsDesktop } from '../search/utils/useSsrMedia';

type HotelPageSimilarHotelsSectionContentProps = {
  rating: number | undefined;
  hotelCoordinates: LatLng | undefined;
  price: number | undefined;
  noRoomsAvailable: boolean | undefined;
} & HTMLAttributes<HTMLElement>;

export function HotelPageSimilarHotelsSectionContent(props: HotelPageSimilarHotelsSectionContentProps) {
  const { rating, hotelCoordinates, noRoomsAvailable, price, ...rest } = props;
  const { pageType, priceType, taxesIncludedInPrice, internalHotelId } = useInitialParams();
  const { localization } = usePageWrapperData();
  const { isDoublePrice, isDoublePriceDP } = useUserConfig();
  const { locale } = localization;

  const { data: hotels } = useRecommendedHotels();
  const isTabletOrGreater = useIsTabletOrGreater();
  const isDesktop = useIsDesktop();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { t } = useTranslation();
  const { handleHotelChosen } = useCommonClient();
  const urlSearchParams = useUrlSearchParams();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { ref, inView } = useInView({ threshold: 0, triggerOnce: true });

  useEffect(() => {
    if (hotels?.length && inView) {
      sendCuriosityClickEvent({
        event_name: 'recommended_hotel_shown',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels, inView, noRoomsAvailable]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [internalHotelId]);

  const onHotelCardClick = useCallback(
    (_: React.SyntheticEvent, hotelData: RecommendedHotelData, cardNumber: number) => {
      trackHotelCardClick(cardNumber);
      sendCuriosityClickEvent({
        event_name: 'click_recommended_hotel',
        event_context: cardNumber.toString(),
      });
      sendMetricIncrement(
        noRoomsAvailable === true
          ? `app.memsearch.${pageType}.similar_hotels_no_result_page.hotel_card_click`
          : `app.memsearch.${pageType}.similar_hotels_normal_page.hotel_card_click`
      );

      if (isDesktop) {
        window.open(getNewHotelUrl(hotelData, internalHotelId));
      } else {
        handleHotelChosen(hotelData.internalHotelId.toString(), {
          refreshSearch: true,
          subSourceType: SubSourceType.HotelDetailRecommendedHotels,
          scrollToTop: true,
          generateClickId: false,
        });
      }
    },
    [noRoomsAvailable]
  );

  // Ordering the recommended hotels
  // First hotel recommended: close to current hotel, same price (up to 5%) and same or more trust you rating
  // Second hotel recommended: close to city center, same price (up to 10%) and same or more trust you rating
  const recommendedHotelsMemo = useMemo(() => {
    let recommendedHotels = hotels?.filter((hotel) => hotel.internal_id_hotel !== internalHotelId);
    if (recommendedHotels?.length && !noRoomsAvailable && price && rating && hotelCoordinates) {
      // Adding distances to hotel (first recommended) and to city center (second recommended)
      // and the distanceText with the distance to the city center (rest of the recommended hotels will have the distance to the city center too)
      const hotelsWithDistance: MemSearchProductDataWithDistance[] = recommendedHotels?.map((hotel) => {
        const { value, unit } = getDistanceFromKm(hotel.distance_km, localization);
        const { latitude, longitude } = hotel;
        return {
          ...hotel,
          distanceToHotel: directLineDistance({ latitude, longitude }, hotelCoordinates),
          distanceText: t('vacation.select.details.distancehotelcitycenter.title', {
            distance: value?.toString(),
            unit,
          }),
        };
      });

      let firstRecommendedNearby = hotelsWithDistance
        ?.filter(
          (hotel) =>
            hotel.price >= price * 0.95 &&
            hotel.price <= price * 1.05 && // up to -/+ 5%
            hotel.rating >= rating // same trust you rating
        )
        ?.sort((a, b) => a.distanceToHotel - b.distanceToHotel)?.[0];

      const secondRecommendedNearby = hotelsWithDistance
        ?.filter(
          (hotel) =>
            hotel.price >= price * 0.9 &&
            hotel.price <= price * 1.1 && // up to -/+ 10%
            hotel.rating >= rating && // same trust you rating
            hotel.internal_id_hotel !== firstRecommendedNearby?.internal_id_hotel
        )
        ?.sort((a, b) => a.distance_km - b.distance_km)?.[0];

      const hotelsWithoutFirstsRecommended = hotelsWithDistance?.filter(
        (hotel) => hotel !== firstRecommendedNearby && hotel !== secondRecommendedNearby
      );

      // Creates the final recommended hotel list with first and second hotels created and the rest of the hotel
      // Updates the fist recommended hotel's distanceText with the distance to the hotel (unique hotel with this info)
      firstRecommendedNearby = firstRecommendedNearby || hotelsWithoutFirstsRecommended?.shift();
      const { value, unit } = getDistanceFromM(firstRecommendedNearby?.distanceToHotel, localization);
      recommendedHotels = [
        firstRecommendedNearby && {
          ...{
            ...firstRecommendedNearby,
            distanceText: t('vacation.select.details.distancehoteltohotel.title', {
              distance: value?.toString(),
              unit,
            }),
          },
        },
        secondRecommendedNearby || hotelsWithoutFirstsRecommended?.shift(),
        ...hotelsWithoutFirstsRecommended,
      ].filter((hotel) => !!hotel);
    }

    return recommendedHotels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels, internalHotelId]);

  return recommendedHotelsMemo?.length ? (
    <HotelPageContentSection {...rest} sectionRef={ref} title={t('vacation.select.section.similarhotels.title')}>
      <ConditionWrapper
        condition={!!isTabletOrGreater}
        // eslint-disable-next-line react/no-unstable-nested-components
        match={(children) => (
          <ScrollableHorizontalWithDots
            slides={recommendedHotelsMemo?.length ?? 0}
            showOverlay={isTabletOrGreater}
            containerRef={scrollContainerRef}
            css={`
              .overlayRight {
                margin: -24px;
              }
              .overlayLeft {
                margin: -24px;
              }

              // Keep space for shadows of the cards
              ${MainContainer} {
                padding: 24px;
                margin: -24px;
              }
            `}
          >
            {children}
          </ScrollableHorizontalWithDots>
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        other={(children) => (
          <ScrollableHorizontal
            containerRef={scrollContainerRef}
            css={`
              display: flex;
              column-gap: 16px;
              // padding and margin are needed to not cut the shadows of the cards
              margin: 0 -16px -16px;
              padding: 0 16px 16px;
              scroll-snap-type: x mandatory;

              & > div {
                scroll-snap-align: center;
                scroll-snap-stop: always;
              }
            `}
          >
            {children}
          </ScrollableHorizontal>
        )}
      >
        <RecommendedHotelsList
          hotels={recommendedHotelsMemo}
          businessProfileId={localization.businessProfile}
          accommodationOnly={urlSearchParams.accommodationOnly}
          locale={locale}
          onHotelCardClick={onHotelCardClick}
          priceType={priceType}
          passengers={urlSearchParams.totalGuestsWithoutInfants}
          taxesIncludedInPrice={taxesIncludedInPrice}
          isDoublePriceDP={isDoublePriceDP}
          isDoublePrice={isDoublePrice}
        />
      </ConditionWrapper>
    </HotelPageContentSection>
  ) : null;
}
function getNewHotelUrl(hotelData: RecommendedHotelData, currentInternalHotelId: number): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(URLParameters.SEARCH_ID);
  url.searchParams.delete(URLParameters.CLICK_ID);
  // CUX 3966: never carry the kids-free badge flag to a similar hotel — it belongs to the origin card.
  url.searchParams.delete(URLParameters.FROM_KIDS_FREE_BADGE);
  url.searchParams.set(URLParameters.BF_SUB_SOURCE, SubSourceType.HotelDetailRecommendedHotels);
  url.searchParams.set(URLParameters.DATE_FROM, hotelData.datefrom);
  url.searchParams.set(URLParameters.DATE_TO, hotelData.dateto);
  url.searchParams.set(
    URLParameters.TRIP_DATES,
    `${hotelData.datefrom.split('-').join('')}-${hotelData.dateto.split('-').join('')}`
  );
  const newPathName = url.pathname.replace(`/${currentInternalHotelId}`, `/${hotelData.internalHotelId}`);
  return url.toString().replace(url.pathname, newPathName);
}
